import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Collapse } from 'antd';
import debug from 'debug';
import { Icons } from 'icons';
import { clone, cloneDeep, uniq } from 'lodash-es';

import ItemTypeIcon from 'components/common/item-type-icon';
import { classnames } from 'lib/classnames';
import fetch from 'lib/fetch';
import useI18n from 'lib/hooks/useI18n';
import { ObjectId } from 'lib/types/models';
import { ResultType, WorkflowDataProps } from 'lib/types/workflow';

import ChangeOperatorItem from './changeOperatorItem';

import styles from './index.module.less';

const cx = classnames(styles);

export type SelectTransitionType = {
  name: string;
  source: {
    key: string;
    name: string;
    type?: string;
  };
  target: {
    key: string;
    name: string;
    type?: string;
  };
};

interface ChangeOperatorProps {
  items: Parse.Attributes[];
  moveWorkspace: Parse.Attributes;
  moveItemType: any;
  itemTypeScreenSchemeId: ObjectId;
  workflowData: WorkflowDataProps;
  updateErrorMessage: (result: ResultType) => void;
}

export const DEFAULT_FLOW_MESSAGE: ResultType = { result: false, message: '' };
const DEFAULT_SELECT_TRANSITION = { transition: null, values: {} };

const log = debug('changeData:content');

function getRequireFieldBehaviors(fieldBehaviors, values) {
  log('get require field behavior', fieldBehaviors, values);
  const keys = Object.keys(fieldBehaviors);
  const requireFields = keys
    .filter(key => {
      const isRequire = fieldBehaviors[key].behaviors.includes('required');
      const fieldValue = values[key];
      const hasValue = Array.isArray(fieldValue) ? fieldValue.length : fieldValue;
      return isRequire && !hasValue;
    })
    .map(key => fieldBehaviors[key].field);
  log('require fields', requireFields);
  return requireFields;
}

const ChangeOperator: React.ForwardRefRenderFunction<{ toNext: () => any }, ChangeOperatorProps> = (
  { items, moveWorkspace, moveItemType, itemTypeScreenSchemeId, workflowData, updateErrorMessage },
  ref,
) => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [allWorkflow, setAllWorkflow] = useState({});
  const [allRequireFields, setAllRequireFields] = useState({});
  const [flowRequireFields, setFlowRequireFields] = useState({});
  const [workflowIsChanges, setWorkflowIsChanges] = useState({});
  const [validate, setValidate] = useState({});
  const [errorData, setErrorData] = useState({});
  const [itemFlowMessage, setItemFlowMessage] = useState({});

  const updateItemErrorMessage = useCallback(
    (itemId: string, result: ResultType) => {
      itemFlowMessage[itemId] = result;
      updateErrorMessage(DEFAULT_FLOW_MESSAGE);
      for (const key of Object.keys(itemFlowMessage)) {
        if (!itemFlowMessage[key].result) {
          updateErrorMessage(itemFlowMessage[key]);
          break;
        }
      }
      setItemFlowMessage(cloneDeep(itemFlowMessage));
    },
    [itemFlowMessage, updateErrorMessage],
  );

  // 获取当前所有事项的工作流
  const getAllWorkflows = useCallback(
    async (ids: string[]): Promise<Record<string, boolean>> => {
      const workflows = await fetch.$post(`/workflows/items`, { ids });
      setAllWorkflow(workflows);
      Object.keys(workflows).forEach(key => {
        workflowIsChanges[key] = workflowData?.objectId !== workflows[key].objectId;
      });
      setWorkflowIsChanges(clone(workflowIsChanges));
      return workflowIsChanges;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workflowData?.objectId],
  );

  // 获取当前所有事项的安全级别方案
  const getSecurityLevelChangeData = useCallback(
    async (itemDetails: { id: string; workspace: string }[]): Promise<Record<string, boolean>> => {
      const workspaces = uniq(itemDetails.map(item => item.workspace));
      const securityLevelSchemes = await fetch.$post(`/securityLevelSchemes/workspaces`, { workspaces });
      const securityLevelData = {};
      itemDetails.forEach(item => {
        securityLevelData[item.id] =
          moveWorkspace?.securityLevelScheme?.objectId !== securityLevelSchemes[item.workspace];
      });
      return securityLevelData;
    },
    [moveWorkspace?.securityLevelScheme?.objectId],
  );

  useEffect(() => {
    const getItemFieldBehavior = async () => {
      try {
        // 更新工作流内容
        const workflowIsChangeData = await getAllWorkflows(items.map(item => item.objectId));
        const securityLevelChangeData = await getSecurityLevelChangeData(
          items.map(item => ({ id: item.objectId, workspace: item.workspace.objectId })),
        );
        // 获取字段数据
        const fieldBehaviors = await fetch.$get(
          `/fieldBehavior/itemType/${moveItemType?.value}/screenScheme/${itemTypeScreenSchemeId}`,
        );
        log('field behavior', fieldBehaviors);
        const requiredFields = {};
        const validateData = {};
        for (const item of items) {
          const requireValues = {};
          const fields = getRequireFieldBehaviors(fieldBehaviors, item.values || {});
          log('require fields', fields, requireValues);
          requiredFields[item.objectId] = fields;
          for (const field of fields) {
            if (field.property?.defaultValue) {
              if (
                (Array.isArray(field.property.defaultValue) && field.property.defaultValue.length) ||
                !Array.isArray(field.property.defaultValue)
              ) {
                requireValues[field.key] = field.property.defaultValue;
              }
            }
          }
          if (!fields?.length) {
            // 计算每个事项的验证过程
            const checkData = {
              check: !workflowIsChangeData?.[item.objectId],
              changeFields: DEFAULT_SELECT_TRANSITION.values,
              transition: DEFAULT_SELECT_TRANSITION.transition,
              securityLevelSchemeIsChange: securityLevelChangeData?.[item.objectId],
            };
            validateData[item.objectId] = checkData;
          } else {
            let nextFlag = false;
            const requireKeys = Object.keys(requireValues);
            if (requireKeys.length) {
              nextFlag = requireKeys.length === fields.length;
            }
            const checkData = {
              check: nextFlag && !workflowIsChangeData?.[item.objectId],
              changeFields: requireValues,
              transition: DEFAULT_SELECT_TRANSITION.transition,
              securityLevelSchemeIsChange: securityLevelChangeData?.[item.objectId],
            };
            validateData[item.objectId] = checkData;
          }
        }
        setAllRequireFields(cloneDeep(requiredFields));
        setValidate(cloneDeep(validateData));
      } catch (error) {
        console.error(error.message);
      } finally {
        setLoading(false);
      }
    };
    getItemFieldBehavior();
    // 这个effect的影响逻辑只关联下面这些变量，其他不影响的disabled掉
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, moveItemType, itemTypeScreenSchemeId]);

  const onFieldsChange = useCallback(
    (itemId: string, { transition, values, flowValues = [] }: any, nextFlag: boolean) => {
      validate[itemId] = {
        check: nextFlag,
        changeFields: values,
        flowChangeFields: flowValues,
        transition,
        securityLevelSchemeIsChange: validate[itemId].securityLevelSchemeIsChange,
      };
      setValidate(cloneDeep(validate));
    },
    [validate],
  );

  const validateForm = useCallback(() => {
    const errorData = {};
    Object.keys(validate).forEach(key => {
      if (validate[key].check) {
        errorData[key] = { type: '', message: '' };
      } else {
        errorData[key] = {
          type: 'error',
          message:
            !validate[key].transition && workflowIsChanges[key]
              ? t('pages.itemMove.workflow.validate.error')
              : t('pages.itemMove.field.validate.error'),
        };
      }
    });
    setErrorData(errorData);
  }, [t, validate, workflowIsChanges]);

  // 所需字段变更修改
  const fieldsChange = useCallback(
    (itemId: string, fields: any[], flowFields: any[]) => {
      allRequireFields[itemId] = fields;
      flowRequireFields[itemId] = flowFields;
      setAllRequireFields(allRequireFields);
      setFlowRequireFields(flowRequireFields);
    },
    [allRequireFields, flowRequireFields],
  );

  const checkNext = useMemo(() => Object.keys(validate).every(key => validate[key].check), [validate]);

  const toNext = useCallback(() => {
    if (!checkNext) {
      validateForm();
      return false;
    }
    return {
      result: validate,
      requireFields: allRequireFields,
      flowRequireFields: flowRequireFields,
    };
  }, [allRequireFields, checkNext, flowRequireFields, validate, validateForm]);

  useImperativeHandle(ref, () => {
    return {
      toNext,
    };
  }, [toNext]);

  const itemData = useMemo(() => {
    return items.map(item => ({
      key: item.objectId,
      type: errorData[item.objectId]?.type || '',
      label: (
        <div className={cx('page-header')}>
          <div className={cx('header-content')}>
            <ItemTypeIcon name={item.itemType.name} icon={item.itemType.icon} className={cx('item-type-icon')} />
            {item.key}
            <span className={cx('page-header-title')}>{item.name}</span>
          </div>
          {errorData[item.objectId]?.message && (
            <div className={cx('view-error')}>
              <Icons.Assist_Error />
              <span className={cx('text')}>{errorData[item.objectId]?.message}</span>
            </div>
          )}
        </div>
      ),
      children: (
        <div className={cx('change-content')}>
          <div className={cx('content-header')}>
            {item.workspace.objectId !== moveWorkspace.objectId && (
              <div className={cx({ divider: item.itemType.objectId !== moveItemType.value })}>
                {t('pages.itemMove.workspaceMove')}
                {item.workspace.name}
                <Icons.WorkflowArrow className={cx('arrow-icon')} />
                {moveWorkspace.name}
              </div>
            )}
            {item.itemType.objectId !== moveItemType.value && (
              <div>
                {t('pages.itemMove.itemTypeMove')}
                <ItemTypeIcon name={item.itemType.name} icon={item.itemType.icon} className={cx('item-type-icon')} />
                {item.itemType.name}
                <Icons.WorkflowArrow className={cx('arrow-icon')} />
                <ItemTypeIcon name={moveItemType.key} icon={moveItemType.icon} className={cx('item-type-icon')} />
                {moveItemType.key}
              </div>
            )}
          </div>
          <ChangeOperatorItem
            key={item.objectId}
            item={item}
            itemWorkflow={allWorkflow[item.objectId]}
            workflowData={workflowData}
            workflowIsChange={workflowIsChanges[item.objectId]}
            moveItemType={moveItemType.value}
            onChange={onFieldsChange}
            updateRequireFields={fieldsChange}
            updateErrorMessage={updateItemErrorMessage}
            requireFields={allRequireFields?.[item.objectId] || []}
            requireValue={validate?.[item.objectId]?.changeFields || {}}
            loading={loading}
          />
        </div>
      ),
    }));
  }, [
    allRequireFields,
    allWorkflow,
    errorData,
    fieldsChange,
    items,
    loading,
    moveItemType.icon,
    moveItemType.key,
    moveItemType.value,
    moveWorkspace.name,
    moveWorkspace.objectId,
    onFieldsChange,
    t,
    updateItemErrorMessage,
    validate,
    workflowData,
    workflowIsChanges,
  ]);

  return (
    <div className={cx('field-wrap')}>
      <Collapse defaultActiveKey={items.map(item => item.objectId)} items={itemData} />
    </div>
  );
};

export default forwardRef(ChangeOperator);
